import { tryOnScopeDispose } from '@vueuse/core'

export interface UseImpressionObserverReturn {
    /**
     * (Re)scan `container` for `selector` matches and observe them. Safe to call
     * repeatedly — observe() is idempotent per element.
     */
    observe: (container?: HTMLElement | null) => void
}

/**
 * Viewability observer for impression tracking: one IntersectionObserver per
 * list, calling `onImpression` whenever an observed element becomes at least
 * half visible in the viewport.
 */
export function useImpressionObserver(
    selector: string,
    onImpression: (el: HTMLElement) => void,
): UseImpressionObserverReturn {
    let observer: IntersectionObserver | null = null

    function observe(container?: HTMLElement | null) {
        if (!container) return

        if ('IntersectionObserver' in window) {
            observer ??= new IntersectionObserver((entries) => {
                for (const entry of entries) {
                    if (entry.intersectionRatio < 0.5) continue

                    onImpression(entry.target as HTMLElement)
                }
            }, { threshold: 0.5 })

            for (const el of container.querySelectorAll<HTMLElement>(selector)) {
                observer.observe(el)
            }
        }
    }

    tryOnScopeDispose(() => {
        observer?.disconnect()
        observer = null
    })

    return { observe }
}
